/* Cluster model provider catalog with direct Account access management. */
import {
  Check,
  Lightning,
  Lock,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  SlidersHorizontal,
  Terminal,
  Trash,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FormEvent } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { ApiError, apiErrorCode } from '../../api/client';
import {
  useSearchUsers,
  useCreateModelPricingRevision,
  useModelPricingRevisions,
  useSetModelAccountGrant,
} from '../../api/queries';
import type {
  CatalogModel,
  CreateModelProviderInput,
  ModelCapabilities,
  ModelProvider,
  ModelProviderAuthType,
  ModelProviderCatalogMode,
  ProviderModel,
  UpdateModelProviderInput,
} from '../../api/types';
import { Button } from '../../components/Button';
import { SelectField, TextField } from '../../components/Field';
import { Modal } from '../../components/Modal';
import { ProviderIcon } from '../../components/ProviderIcon';
import { StatusLabel } from '../../components/PageLayout';
import { ErrorBlock, LoadingBlock } from '../../components/States';
import { useToast } from '../../components/Toast';
import { DESKTOP_PROVIDERS, desktopProvider } from '../../lib/desktopProviders';
import type { ModelsAdminApi, ModelsScope } from './scope';
import { useModelsAdminApi } from './scope';
import styles from './ModelsCatalog.module.css';
import { ModelAuthorizationDialog } from './ModelAuthorizationDialog';

const EMPTY_CAPABILITIES: ModelCapabilities = { reasoning: false, tools: false, image: false };

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function contextLabel(contextWindow: number, t: TFunction): string {
  if (contextWindow <= 0) return t('cluster.models.contextUnknown');
  if (contextWindow >= 1000 && contextWindow % 1000 === 0) return t('cluster.models.contextK', { value: contextWindow / 1000 });
  return t('cluster.models.context', { value: contextWindow.toLocaleString() });
}

export interface ModelsCatalogProps {
  scope: ModelsScope;
  /** Controlled provider add/edit dialog state, owned by the parent. */
  editor: ModelProvider | 'new' | null;
  onEditorChange: (next: ModelProvider | 'new' | null) => void;
  /** The cluster catalog can optionally hide its search box. */
  searchable?: boolean;
}

/**
 * The catalog surface: security note, (optional) search toolbar, the configured-
 * providers heading, the provider cards, and the provider add/edit dialog.
 */
export function ModelsCatalog({ scope, editor, onEditorChange, searchable = true }: ModelsCatalogProps) {
  const { t } = useTranslation();
  const api = useModelsAdminApi(scope);
  const providers = api.providersQuery;
  const [search, setSearch] = useState('');
  const [authorization, setAuthorization] = useState<ModelProvider | 'new' | null>(null);
  const [params, setParams] = useSearchParams();
  const requestedProvider = params.get('provider');
  useEffect(() => {
    if (scope.kind !== 'account' || !requestedProvider) return;
    const selected = providers.data?.find(item => item.id === requestedProvider);
    if (selected?.auth_type === 'oauth') setAuthorization(selected);
  }, [requestedProvider, providers.data, scope.kind]);
  const closeAuthorization = () => {
    setAuthorization(null);
    if (requestedProvider) setParams(current => { const next = new URLSearchParams(current); next.delete('provider'); return next; }, { replace: true });
  };

  const query = searchable ? search.trim().toLowerCase() : '';
  const list = providers.data ?? [];
  const filtered = list.map((provider) => {
    if (!query) return provider;
    const providerMatches = `${provider.name} ${provider.kind} ${provider.base_url}`.toLowerCase().includes(query);
    const models = provider.models.filter((model) => `${model.name} ${model.model_id} ${model.source} ${Object.entries(model.capabilities).filter(([, enabled]) => enabled).map(([name]) => name).join(' ')}`.toLowerCase().includes(query));
    return providerMatches ? provider : { ...provider, models };
  }).filter((provider) => !query || provider.models.length > 0 || `${provider.name} ${provider.kind} ${provider.base_url}`.toLowerCase().includes(query));
  const totalModels = list.reduce((sum, provider) => sum + provider.models.length, 0);
  const visibleModels = filtered.reduce((sum, provider) => sum + provider.models.length, 0);
  const totalAccountGrants = list.reduce((sum, provider) =>
    sum + provider.models.reduce((modelSum, model) => modelSum + (model.granted_account_ids?.length ?? 0), 0), 0);
  const summary = scope.kind === 'account' ? t('cloudRefresh.personalModelsDescription') : t('cluster.models.providersSummary', {
    providers: list.length,
    accountGrants: totalAccountGrants,
  });

  return (
    <section className={styles.catalog} aria-labelledby="provider-catalog-title">
      <div className={styles.security}><Lock size={14} aria-hidden="true" /><span>{t('cluster.models.securityNote')}</span></div>
      {searchable && (
        <div className={styles.toolbar}>
          <label className={styles.search}>
            <MagnifyingGlass size={14} aria-hidden="true" />
            <span className={styles.srOnly}>{t('cluster.models.filterLabel')}</span>
            <input type="search" aria-label={t('cluster.models.filterLabel')} placeholder={t('cluster.models.filterPlaceholder')} value={search} onChange={(event) => setSearch(event.target.value)} />
          </label>
          <span className={styles.result}><b>{visibleModels}</b> {t('cluster.models.resultCount', { total: totalModels })}</span>
        </div>
      )}
      <div className={styles.heading}><div><h2 id="provider-catalog-title">{t('cluster.models.configuredProviders')}</h2><p>{summary}</p></div>{scope.kind === 'account' && <Button size="sm" variant="ghost" onClick={() => setAuthorization('new')}>{t('modelAuthorization.connect')}</Button>}</div>

      {providers.isLoading ? <LoadingBlock label={t('cluster.models.loadingProviders')} /> : providers.isError ? (
        <ErrorBlock error={providers.error} onRetry={() => providers.refetch()} title={t('cluster.models.loadProvidersError')} />
      ) : filtered.length === 0 ? (
        <div className={styles.searchEmpty} role="status"><MagnifyingGlass size={24} aria-hidden="true" /><strong>{list.length ? t('cluster.models.noMatches') : t('cluster.models.noProviders')}</strong><span>{list.length ? t('cluster.models.noMatchesHint') : t('cluster.models.noProvidersHint')}</span></div>
      ) : (
        <div className={styles.stack}>{filtered.map((provider) => <ProviderCard key={provider.id} api={api} provider={provider} onEdit={() => provider.auth_type === 'oauth' ? setAuthorization(provider) : onEditorChange(provider)} />)}</div>
      )}

      <ModelAuthorizationDialog open={authorization !== null} provider={authorization === 'new' ? null : authorization} onClose={closeAuthorization} />
      <ProviderDialog
        api={api}
        provider={editor === 'new' ? null : editor}
        configuredKinds={list.map((configured) => configured.kind)}
        open={editor !== null}
        onClose={() => onEditorChange(null)}
      />
    </section>
  );
}

function ProviderCard({ api, provider, onEdit }: { api: ModelsAdminApi; provider: ModelProvider; onEdit: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const verify = api.verifyProvider;
  const remove = api.deleteProvider;
  const [customOpen, setCustomOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const authLabel = provider.auth_type === 'oauth' ? t(`modelAuthorization.state.${provider.authorization?.state ?? 'requires_reauth'}`) : provider.auth_type === 'service_identity' ? t('cluster.models.authServiceIdentity') : provider.api_key_set ? t('cluster.models.authKeySet') : provider.auth_type === 'none' ? t('cluster.models.authKeyless') : t('cluster.models.authKeyMissing');
  const authTone: 'success' | 'warning' = provider.auth_type === 'oauth' ? (provider.authorization?.state === 'ready' ? 'success' : 'warning') : provider.auth_type !== 'api_key' || provider.api_key_set ? 'success' : 'warning';
  const verified = provider.last_verified_at && !provider.last_verification_error;

  const testProvider = () => verify.mutate(provider.id, {
    onSuccess: (result) => toast.push({ kind: 'success', message: result.catalog_available ? t('cluster.models.verifiedIn', { ms: result.latency_ms }) : t('cluster.models.reachableIn', { ms: result.latency_ms }) }),
    onError: (error) => toast.push({ kind: 'error', message: errorMessage(error, t('cluster.models.verifyError')) }),
  });

  const deleteProvider = () => {
    if (!window.confirm(t('cluster.models.removeConfirm', { name: provider.name }))) return;
    remove.mutate(provider.id, {
      onSuccess: () => toast.push({ kind: 'success', message: t('cluster.models.providerRemoved') }),
      onError: (error) => toast.push({ kind: 'error', message: errorMessage(error, t('cluster.models.removeError')) }),
    });
  };

  return (
    <article className={styles.providerCard} data-testid={`provider-card-${provider.id}`}>
      <header className={styles.providerHead}>
        <ProviderIcon kind={provider.kind} name={provider.name} />
        <div className={styles.providerCopy}>
          <div className={styles.providerTitle}><h2>{provider.name}</h2>{provider.kind === 'custom' && <StatusLabel>{t('cluster.models.customLabel')}</StatusLabel>}<StatusLabel tone={authTone}>{authLabel}</StatusLabel></div>
          <p><span className={styles.mono}>{provider.base_url}</span><span aria-hidden="true"> · </span>{provider.auth_type === 'oauth' ? provider.authorization?.login || 'ChatGPT' : provider.auth_type === 'service_identity' ? t('cluster.models.credKeylessIdentity') : provider.api_key_set ? t('cluster.models.credConfigured') : provider.auth_type === 'none' ? t('cluster.models.credNoneRequired') : t('cluster.models.credRequired')}</p>
          {provider.last_verification_error && <p className={styles.providerError}>{t('cluster.models.lastTest', { error: provider.last_verification_error })} <button type="button" className={styles.textButton} onClick={onEdit}>{t('cloudRefresh.updateCredentials')}</button></p>}
        </div>
        <div className={styles.providerActions}>
          <Button size="sm" variant="ghost" onClick={testProvider} loading={verify.isPending}>{verified && <Check size={13} aria-hidden="true" />}{verified ? t('cluster.models.verified') : t('cluster.models.test')}</Button>
          {provider.auth_type === 'oauth' && <Button size="sm" variant="ghost" onClick={onEdit}>{t('modelAuthorization.reauthorize')}</Button>}
          <button className={styles.iconButton} type="button" aria-label={t('cluster.models.editProviderAria', { name: provider.name })} onClick={onEdit}><SlidersHorizontal size={16} aria-hidden="true" /></button>
          <button className={styles.iconButton} type="button" aria-label={t('cluster.models.removeProviderAria', { name: provider.name })} onClick={deleteProvider} disabled={remove.isPending}><Trash size={16} aria-hidden="true" /></button>
        </div>
      </header>
      <div className={styles.providerModels}>
        <div className={styles.modelBar}>
          <span><b>{provider.models.length}</b> {provider.models.length === 1 ? (provider.models[0]?.source === 'custom' ? t('cluster.models.customModel') : t('cluster.models.configuredModel')) : t('cluster.models.configuredModels')}</span>
          <div className={styles.modelActions}>
            {provider.catalog_mode === 'disabled' ? (
              <button className={styles.textButton} type="button" disabled><MagnifyingGlass size={13} aria-hidden="true" />{t('cluster.models.catalogUnavailable')}</button>
            ) : (
              <button className={styles.textButton} type="button" onClick={() => setCatalogOpen(true)}><MagnifyingGlass size={13} aria-hidden="true" />{t('cluster.models.browseCatalog')}</button>
            )}
            <Button size="sm" onClick={() => setCustomOpen(true)}><Plus size={13} aria-hidden="true" />{t('cluster.models.customModelAction')}</Button>
          </div>
        </div>
        {provider.models.length > 0 && <div className={styles.modelList}>{provider.models.map((model) => <ModelRow key={model.id} api={api} provider={provider} model={model} />)}</div>}
        {provider.catalog_mode === 'disabled' && <p className={styles.limitNote}>{t('cluster.models.limitNote')}</p>}
      </div>
      <CustomModelDialog api={api} provider={provider} open={customOpen} onClose={() => setCustomOpen(false)} />
      <CatalogDialog api={api} provider={provider} open={catalogOpen} onClose={() => setCatalogOpen(false)} />
    </article>
  );
}

function CapabilityChips({ model }: { model: ProviderModel }) {
  const { t } = useTranslation();
  return (
    <span className={styles.capabilities} aria-label={t('cluster.models.capabilitiesAria')}>
      {model.capabilities.reasoning && <span><Lightning size={11} aria-hidden="true" />{t('cluster.models.capReasoning')}</span>}
      {model.capabilities.tools && <span><Terminal size={11} aria-hidden="true" />{t('cluster.models.capTools')}</span>}
      {model.capabilities.image && <span>{t('cluster.models.capImage')}</span>}
    </span>
  );
}

function ModelRow({ api, provider, model }: { api: ModelsAdminApi; provider: ModelProvider; model: ProviderModel }) {
  const { t } = useTranslation();
  const [grantsOpen, setGrantsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);

  const accountGrantCount = (model.granted_account_ids ?? []).length;
  return (
    <div className={styles.modelRow}>
      <span className={styles.modelCopy}><strong>{model.name}</strong><small>{model.model_id} · {contextLabel(model.context_window, t)}</small></span>
      <CapabilityChips model={model} />
      {api.scope.kind === 'cluster' && <span className={styles.grantCount} data-testid={`grant-count-${model.id}`}>
        <span><b>{accountGrantCount}</b><small>{t('cluster.models.accountGrants', { count: accountGrantCount })}</small></span>
      </span>}
      <div className={styles.modelControls}>
        {api.scope.kind === 'account' && <><Button size="sm" variant="ghost" disabled={api.updateClusterModel.isPending} onClick={() => api.updateClusterModel.mutate({ id: model.id, input: { enabled: model.enabled === false } })}>{t(model.enabled === false ? 'cloudRefresh.enable' : 'cloudRefresh.disable')}</Button><button type="button" className={styles.iconButton} aria-label={t('cloudRefresh.deleteModel', { name: model.name })} disabled={api.deleteModel.isPending} onClick={() => { if(window.confirm(t('cloudRefresh.deleteModelConfirm', { name: model.name }))) api.deleteModel.mutate({ providerId: provider.id, id: model.id }); }}><Trash size={15} /></button></>}

        {api.scope.kind === 'cluster' && <Button size="sm" variant="ghost" onClick={() => setPricingOpen(true)}>{t('usage.pricing')}</Button>}
        {api.scope.kind === 'cluster' && <Button size="sm" variant="ghost" onClick={() => setGrantsOpen(true)}>{t('cluster.models.manageAccess')}</Button>}
        <button className={styles.iconButton} type="button" aria-label={t('projectSettings.models.editModelAria', { name: model.name })} onClick={() => setEditOpen(true)}><PencilSimple size={16} aria-hidden="true" /></button>
      </div>
      {api.scope.kind === 'cluster' && <PricingRevisionDialog model={model} open={pricingOpen} onClose={() => setPricingOpen(false)} />}
      {api.scope.kind === 'cluster' && <AccessDialog model={model} open={grantsOpen} onClose={() => setGrantsOpen(false)} />}
      {api.updateClusterModel.isError && <p role="alert">{errorMessage(api.updateClusterModel.error, t('projectSettings.models.saveModelError'))}</p>}
      {api.deleteModel.isError && <p role="alert">{errorMessage(api.deleteModel.error, t('cluster.models.removeError'))}</p>}
      <CustomModelDialog api={api} provider={provider} model={model} open={editOpen} onClose={() => setEditOpen(false)} />
    </div>
  );
}

export function PricingRevisionDialog({
  model,
  open,
  onClose,
}: {
  model: ProviderModel;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const revisions = useModelPricingRevisions(model.id, open);
  const create = useCreateModelPricingRevision(model.id);
  const [currency, setCurrency] = useState('USD');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [rates, setRates] = useState({ input: '', output: '', cacheRead: '', cacheWrite: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setCurrency('USD');
    setEffectiveAt(dateTimeLocalValue(new Date()));
    setRates({ input: '', output: '', cacheRead: '', cacheWrite: '' });
    setError('');
  }, [open]);

  const toMicros = (value: string): number | null => {
    if (value.trim() === '') return null;
    const parsed = Number(value);
    const micros = Math.round(parsed * 1_000_000);
    return Number.isFinite(parsed) && parsed >= 0 && Number.isSafeInteger(micros)
      ? micros
      : Number.NaN;
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const values = {
      input_micros_per_million: toMicros(rates.input),
      output_micros_per_million: toMicros(rates.output),
      cache_read_micros_per_million: toMicros(rates.cacheRead),
      cache_write_micros_per_million: toMicros(rates.cacheWrite),
    };
    if (!/^[A-Z]{3,8}$/.test(currency.trim().toUpperCase()) ||
      !effectiveAt ||
      Object.values(values).some((value) => typeof value === 'number' && Number.isNaN(value)) ||
      Object.values(values).every((value) => value == null)) {
      setError(t('usage.pricingValidation'));
      return;
    }
    create.mutate({
      currency: currency.trim().toUpperCase(),
      ...values,
      effective_at: new Date(effectiveAt).toISOString(),
    }, {
      onSuccess: () => {
        setRates({ input: '', output: '', cacheRead: '', cacheWrite: '' });
        setError('');
      },
      onError: (cause) => setError(errorMessage(cause, t('usage.pricingCreateError'))),
    });
  };
  const formId = `pricing-${model.id}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('usage.pricingTitle', { model: model.name })}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.done')}</Button>
          <Button
            loading={create.isPending}
            onClick={() => document.getElementById(formId)?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))}
          >
            {t('usage.addPricingRevision')}
          </Button>
        </>
      )}
    >
      <p className={styles.dialogIntro}>{t('usage.pricingImmutable')}</p>
      <form id={formId} className={styles.dialogForm} onSubmit={submit}>
        <div className={styles.pricingIdentity}>
          <TextField label={t('usage.currency')} value={currency} onChange={(event) => setCurrency(event.target.value.toUpperCase())} />
          <TextField label={t('usage.effectiveAt')} type="datetime-local" value={effectiveAt} onChange={(event) => setEffectiveAt(event.target.value)} />
        </div>
        <div className={styles.pricingRates}>
          <PricingRate label={t('usage.inputRate')} value={rates.input} onChange={(input) => setRates((current) => ({ ...current, input }))} />
          <PricingRate label={t('usage.outputRate')} value={rates.output} onChange={(output) => setRates((current) => ({ ...current, output }))} />
          <PricingRate label={t('usage.cacheReadRate')} value={rates.cacheRead} onChange={(cacheRead) => setRates((current) => ({ ...current, cacheRead }))} />
          <PricingRate label={t('usage.cacheWriteRate')} value={rates.cacheWrite} onChange={(cacheWrite) => setRates((current) => ({ ...current, cacheWrite }))} />
        </div>
        {error && <p className={styles.formError} role="alert">{error}</p>}
      </form>
      <div className={styles.pricingHistory}>
        <h3>{t('usage.pricingHistory')}</h3>
        {revisions.isLoading ? <LoadingBlock label={t('usage.loadingPricing')} /> : revisions.isError ? (
          <ErrorBlock error={revisions.error} onRetry={() => void revisions.refetch()} title={t('usage.pricingLoadError')} />
        ) : (revisions.data ?? []).length === 0 ? (
          <p className={styles.dialogIntro}>{t('usage.noPricingRevisions')}</p>
        ) : (
          <ol>
            {(revisions.data ?? []).map((revision) => (
              <li key={revision.id}>
                <strong>{revision.currency} · {new Date(revision.effective_at).toLocaleString()}</strong>
                <small>
                  {[
                    revision.input_micros_per_million == null ? null : `${t('usage.input')} ${revision.input_micros_per_million / 1_000_000}`,
                    revision.output_micros_per_million == null ? null : `${t('usage.output')} ${revision.output_micros_per_million / 1_000_000}`,
                    revision.cache_read_micros_per_million == null ? null : `${t('usage.cacheRead')} ${revision.cache_read_micros_per_million / 1_000_000}`,
                    revision.cache_write_micros_per_million == null ? null : `${t('usage.cacheWrite')} ${revision.cache_write_micros_per_million / 1_000_000}`,
                  ].filter(Boolean).join(' · ')}
                </small>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Modal>
  );
}

function dateTimeLocalValue(value: Date): string {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function PricingRate({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <TextField
      label={label}
      type="number"
      min="0"
      step="0.000001"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder="0.00"
    />
  );
}

function CapabilityFields({ value, onChange }: { value: ModelCapabilities; onChange: (next: ModelCapabilities) => void }) {
  const { t } = useTranslation();
  return (
    <fieldset className={styles.capabilityFields}>
      <legend>{t('cluster.models.capabilitiesLegend')}</legend>
      <label><input type="checkbox" checked={value.reasoning} onChange={(event) => onChange({ ...value, reasoning: event.target.checked })} />{t('cluster.models.capReasoning')}</label>
      <label><input type="checkbox" checked={value.tools} onChange={(event) => onChange({ ...value, tools: event.target.checked })} />{t('cluster.models.capToolUse')}</label>
      <label><input type="checkbox" checked={value.image} onChange={(event) => onChange({ ...value, image: event.target.checked })} />{t('cluster.models.capImageInput')}</label>
    </fieldset>
  );
}

/**
 * Add or edit a cluster model.
 * Editing PATCHes name / context_window / capabilities (the model_id is fixed).
 */
function CustomModelDialog({ api, provider, open, onClose, model }: { api: ModelsAdminApi; provider: ModelProvider; open: boolean; onClose: () => void; model?: ProviderModel }) {
  const { t } = useTranslation();
  const editing = !!model;
  const toast = useToast();
  const [name, setName] = useState('');
  const [modelId, setModelId] = useState('');
  const [contextWindow, setContextWindow] = useState('');
  const [capabilities, setCapabilities] = useState(EMPTY_CAPABILITIES);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!open) return;
    setName(model?.name ?? '');
    setModelId(model?.model_id ?? '');
    setContextWindow(model && model.context_window > 0 ? String(model.context_window) : '');
    setCapabilities(model ? { ...model.capabilities } : EMPTY_CAPABILITIES);
    setError(undefined);
  }, [open, model]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsedContext = contextWindow === '' ? 0 : Number(contextWindow);
    if (!name.trim() || (!editing && !modelId.trim())) { setError(t('cluster.models.customValidationRequired')); return; }
    if (!Number.isFinite(parsedContext) || parsedContext < 0) { setError(t('cluster.models.customValidationContext')); return; }
    if (editing && model) {
      const input = { name: name.trim(), context_window: parsedContext, capabilities };
      const options = {
        onSuccess: () => { toast.push({ kind: 'success', message: t('projectSettings.models.modelSaved') }); onClose(); },
        onError: (cause: unknown) => setError(errorMessage(cause, t('projectSettings.models.saveModelError'))),
      };
      api.updateClusterModel.mutate({ id: model.id, input }, options);
      return;
    }
    api.createModel.mutate({ providerId: provider.id, input: { name: name.trim(), model_id: modelId.trim(), context_window: parsedContext, capabilities, source: 'custom' } }, {
      onSuccess: () => { toast.push({ kind: 'success', message: t('cluster.models.customAdded') }); setName(''); setModelId(''); setContextWindow(''); setCapabilities(EMPTY_CAPABILITIES); setError(undefined); onClose(); },
      onError: (cause) => setError(errorMessage(cause, t('cluster.models.addModelError'))),
    });
  };

  const formId = editing && model ? `custom-model-edit-${model.id}` : `custom-model-${provider.id}`;
  const pending = editing ? api.updateClusterModel.isPending : api.createModel.isPending;
  return (
    <Modal open={open} onClose={onClose} title={editing && model ? t('projectSettings.models.editDialogTitle', { name: model.name }) : t('cluster.models.customDialogTitle', { name: provider.name })} footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button><Button variant="primary" onClick={() => document.getElementById(formId)?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))} loading={pending}>{editing ? t('projectSettings.models.saveModel') : t('cluster.models.addCustomModel')}</Button></>}>
      <form id={formId} className={styles.dialogForm} onSubmit={submit}>
        <TextField label={t('cluster.models.displayNameLabel')} required value={name} onChange={(event) => setName(event.target.value)} placeholder={t('cluster.models.displayNamePlaceholder')} />
        {!editing && <TextField label={t('cluster.models.modelIdLabel')} required value={modelId} onChange={(event) => setModelId(event.target.value)} placeholder="coding-plan" hint={t('cluster.models.modelIdHint')} />}
        <TextField label={t('cluster.models.contextWindowLabel')} type="number" min={0} value={contextWindow} onChange={(event) => setContextWindow(event.target.value)} placeholder="32000" />
        <CapabilityFields value={capabilities} onChange={setCapabilities} />
        {error && <p className={styles.formError} role="alert">{error}</p>}
      </form>
    </Modal>
  );
}

function CatalogDialog({ api, provider, open, onClose }: { api: ModelsAdminApi; provider: ModelProvider; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const catalog = api.useCatalog(provider.id, open);
  const create = api.createModel;
  const toast = useToast();
  const add = (model: CatalogModel) => create.mutate({ providerId: provider.id, input: { name: model.name || model.id, model_id: model.id, context_window: model.context_window, capabilities: model.capabilities, source: 'catalog' } }, {
    onSuccess: () => toast.push({ kind: 'success', message: t('cluster.models.modelAdded', { name: model.name || model.id }) }),
    onError: (error) => toast.push({ kind: 'error', message: errorMessage(error, t('cluster.models.addCatalogError')) }),
  });
  const syncMetadata = (model: CatalogModel, configured: ProviderModel) => {
    const input = {
      name: configured.name,
      context_window: model.context_window,
      capabilities: model.capabilities,
    };
    const options = {
      onSuccess: () => toast.push({ kind: 'success', message: t('cluster.models.metadataSynced', { name: configured.name }) }),
      onError: (error: unknown) => toast.push({ kind: 'error', message: errorMessage(error, t('cluster.models.syncMetadataError')) }),
    };
    api.updateClusterModel.mutate({ id: configured.id, input }, options);
  };
  const updatePending = api.updateClusterModel.isPending;
  return (
    <Modal open={open} onClose={onClose} title={t('cluster.models.catalogDialogTitle', { name: provider.name })}>
      {catalog.isLoading ? <LoadingBlock label={t('cluster.models.loadingCatalog')} /> : catalog.isError ? (
        <div className={styles.catalogError}><strong>{apiErrorCode(catalog.error) === 'catalog_unavailable' ? t('cluster.models.catalogUnavailable') : t('cluster.models.catalogLoadError')}</strong><p>{errorMessage(catalog.error, t('cluster.models.catalogLoadFallback'))}</p><Button onClick={() => catalog.refetch()}>{t('common.retry')}</Button></div>
      ) : (
        <ul className={styles.catalogList}>{(catalog.data ?? []).map((model) => {
          const configured = provider.models.find((item) => item.model_id === model.id);
          const metadataDiffers = !!configured && configured.source === 'catalog' && model.metadata_source === 'models.dev' && (
            configured.context_window !== model.context_window ||
            configured.capabilities.reasoning !== model.capabilities.reasoning ||
            configured.capabilities.tools !== model.capabilities.tools ||
            configured.capabilities.image !== model.capabilities.image
          );
          return (
            <li key={model.id}>
              <span><strong>{model.name || model.id}</strong><small>{model.id} · {contextLabel(model.context_window, t)}</small></span>
              {metadataDiffers ? (
                <Button size="sm" onClick={() => syncMetadata(model, configured)} disabled={updatePending}>{t('cluster.models.syncMetadata')}</Button>
              ) : (
                <Button size="sm" onClick={() => add(model)} disabled={!!configured || create.isPending}>{configured ? t('common.configured') : t('common.add')}</Button>
              )}
            </li>
          );
        })}</ul>
      )}
    </Modal>
  );
}

/** Cluster scope only: one direct Account grant covers Desktop and Cloud runs. */
function AccessDialog({ model, open, onClose }: { model: ProviderModel; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const accounts = useSearchUsers(search, open);
  const setAccountGrant = useSetModelAccountGrant();
  const toast = useToast();
  const grantedAccounts = model.granted_account_ids ?? [];

  useEffect(() => {
    if (!open) return;
    setSearch('');
  }, [open]);

  const toggleAccount = (userId: string, next: boolean) => setAccountGrant.mutate({ modelId: model.id, userId, granted: next }, {
    onError: (error) => toast.push({ kind: 'error', message: errorMessage(error, t('cluster.models.accountGrantUpdateError')) }),
  });

  return (
    <Modal open={open} onClose={onClose} title={t('cluster.models.accessDialogTitle', { name: model.name })} footer={<Button onClick={onClose}>{t('common.done')}</Button>}>
      <p className={styles.dialogIntro}>{t('cluster.models.accessIntro')}</p>
      <section className={styles.accessPane}>
        <p className={styles.dialogIntro}>{t('cluster.models.accountAccessIntro')}</p>
        <label className={styles.accountSearch}>
          <MagnifyingGlass size={15} aria-hidden="true" />
          <span className={styles.srOnly}>{t('cluster.models.accountSearchLabel')}</span>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('cluster.models.accountSearchPlaceholder')} />
        </label>
        {accounts.isLoading ? <LoadingBlock label={t('cluster.models.loadingAccounts')} /> : (accounts.data ?? []).length === 0 ? <p className={styles.dialogIntro}>{t('cluster.models.noAccounts')}</p> : (
          <div className={styles.grantList}>{(accounts.data ?? []).map((account) => {
            const isGranted = grantedAccounts.includes(account.id);
            return <label key={account.id}><input type="checkbox" aria-label={account.display_name} checked={isGranted} disabled={setAccountGrant.isPending} onChange={(event) => toggleAccount(account.id, event.target.checked)} /><span><strong>{account.display_name}</strong><small>{account.is_cluster_admin ? t('cluster.models.clusterAdminAccount') : t('cluster.models.allCloudAndDesktop')}</small></span></label>;
          })}</div>
        )}
      </section>
    </Modal>
  );
}

interface HeaderRow { key: string; value: string }

function ProviderDialog({ api, provider, configuredKinds, open, onClose }: { api: ModelsAdminApi; provider: ModelProvider | null; configuredKinds: string[]; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const create = api.createProvider;
  const update = api.updateProvider;
  const toast = useToast();
  const [mode, setMode] = useState<'registry' | 'custom'>('registry');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState('openai');
  const [baseUrl, setBaseUrl] = useState('');
  const [authType, setAuthType] = useState<ModelProviderAuthType>('api_key');
  const [apiKey, setApiKey] = useState('');
  const [catalogMode, setCatalogMode] = useState<ModelProviderCatalogMode>('auto');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [headerRows, setHeaderRows] = useState<HeaderRow[]>([]);
  const [clearHeaders, setClearHeaders] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!open) return;
    setMode('registry');
    setSelectedProvider('');
    setName(provider?.name ?? '');
    setKind(provider?.kind ?? 'openai');
    setBaseUrl(provider?.base_url ?? '');
    setAuthType(provider?.auth_type ?? 'api_key');
    setApiKey('');
    setCatalogMode(provider?.catalog_mode ?? 'auto');
    setAdvancedOpen(!!provider?.headers_set);
    setHeaderRows([]);
    setClearHeaders(false);
    setError(undefined);
  }, [open, provider]);

  const chooseMode = (next: 'registry' | 'custom') => {
    setMode(next);
    setSelectedProvider('');
    setName('');
    setKind(next === 'custom' ? 'openai' : '');
    setBaseUrl('');
  };

  const chooseProvider = (id: string) => {
    setSelectedProvider(id);
    const preset = desktopProvider(id);
    setName(preset?.name ?? '');
    setKind(preset?.id ?? '');
    setBaseUrl(preset?.baseUrl ?? '');
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !kind.trim() || !baseUrl.trim()) { setError(t('cluster.models.providerValidationRequired')); return; }
    if (!provider && authType === 'api_key' && !apiKey) { setError(t('cluster.models.providerValidationKey')); return; }
    // Collect complete header rows; a half-filled row mirrors the server's 400.
    const headers: Record<string, string> = {};
    let touchedHeaders = false;
    for (const row of headerRows) {
      const key = row.key.trim();
      if (!key && row.value.trim() === '') continue;
      touchedHeaders = true;
      if (!key || row.value.trim() === '') { setError(t('cluster.models.headerValidation')); return; }
      headers[key] = row.value;
    }
    const headerPatch = clearHeaders ? {} : touchedHeaders ? headers : undefined;
    if (provider) {
      const input: UpdateModelProviderInput = { name: name.trim(), kind: kind.trim(), base_url: baseUrl.trim(), auth_type: authType, catalog_mode: catalogMode };
      if (apiKey !== '') input.api_key = apiKey;
      if (headerPatch) input.headers = headerPatch;
      update.mutate({ id: provider.id, input }, {
        onSuccess: () => { toast.push({ kind: 'success', message: t('cluster.models.providerSaved') }); onClose(); },
        onError: (cause) => setError(errorMessage(cause, t('cluster.models.saveProviderError'))),
      });
    } else {
      const input: CreateModelProviderInput = { name: name.trim(), kind: kind.trim(), base_url: baseUrl.trim(), auth_type: authType, api_key: authType === 'api_key' ? apiKey : '', catalog_mode: catalogMode };
      if (headerPatch) input.headers = headerPatch;
      create.mutate(input, {
        onSuccess: () => { toast.push({ kind: 'success', message: t('cluster.models.providerAdded') }); onClose(); },
        onError: (cause) => setError(errorMessage(cause, t('cluster.models.addProviderError'))),
      });
    }
  };
  const setHeaderRow = (index: number, patch: Partial<HeaderRow>) =>
    setHeaderRows((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const formId = provider ? `provider-edit-${provider.id}` : 'provider-add';
  return (
    <Modal open={open} onClose={onClose} title={provider ? t('cluster.models.editProviderTitle', { name: provider.name }) : t('cluster.models.addProviderTitle')} footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button><Button variant="primary" loading={create.isPending || update.isPending} onClick={() => document.getElementById(formId)?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))}>{provider ? t('cluster.models.saveProvider') : t('cluster.models.addProvider')}</Button></>}>
      <form id={formId} className={styles.dialogForm} onSubmit={submit}>
        {!provider && (
          <div className={styles.providerPicker}>
            <div className={styles.providerMode} role="group" aria-label={t('cluster.models.providerSourceLabel')}>
              <button type="button" data-active={mode === 'registry' || undefined} data-testid="provider-mode-registry" onClick={() => chooseMode('registry')}>{t('cluster.models.providerRegistryMode')}</button>
              <button type="button" data-active={mode === 'custom' || undefined} data-testid="provider-mode-custom" onClick={() => chooseMode('custom')}>{t('cluster.models.providerCustomMode')}</button>
            </div>
            {mode === 'registry' && (
              <label className={styles.nativeSelectField}>
                <span>{t('cluster.models.providerSelectLabel')} <b aria-hidden="true">*</b></span>
                <select aria-label={t('cluster.models.providerSelectLabel')} value={selectedProvider} onChange={(event) => chooseProvider(event.target.value)}>
                  <option value="">{t('cluster.models.providerSelectPlaceholder')}</option>
                  {DESKTOP_PROVIDERS.filter((preset) => !configuredKinds.includes(preset.id)).map((preset) => <option key={preset.id} value={preset.id}>{preset.name}</option>)}
                </select>
              </label>
            )}
            {mode === 'registry' && selectedProvider && (
              <div className={styles.providerPreset} data-testid="selected-provider-preset">
                <ProviderIcon kind={kind} name={name} />
                <span><strong>{name}</strong><code>{kind}</code></span>
              </div>
            )}
          </div>
        )}
        {authType === 'api_key' && <TextField label={provider ? t('cluster.models.rotateApiKeyLabel') : t('cluster.models.authApiKey')} type="password" autoComplete="off" value={apiKey} onChange={(event) => setApiKey(event.target.value)} placeholder={provider?.api_key_set ? t('cluster.models.apiKeyPlaceholderKeep') : t('cluster.models.apiKeyPlaceholderEnter')} hint={t('cluster.models.apiKeyHint')} />}
        {(provider || mode === 'custom') && <TextField label={t('cluster.models.providerNameLabel')} required value={name} onChange={(event) => setName(event.target.value)} placeholder={t('cluster.models.providerNamePlaceholder')} />}
        {!provider && mode === 'custom' && <TextField label={t('cluster.models.providerTypeLabel')} required value={kind} onChange={(event) => setKind(event.target.value.toLowerCase())} placeholder="openai" hint={t('cluster.models.providerTypeHint')} />}
        <div className={styles.advanced}>
          <button type="button" className={styles.advancedToggle} aria-expanded={advancedOpen} data-testid="provider-advanced-toggle" onClick={() => setAdvancedOpen((o) => !o)}>
            <SlidersHorizontal size={13} aria-hidden="true" />{t('cluster.models.advancedLabel')}
          </button>
          {advancedOpen && (
            <div className={styles.headerFields}>
              <TextField label={t('cluster.models.baseUrlLabel')} required value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} placeholder="https://api.example.com/v1" />
              <SelectField label={t('cluster.models.authLabel')} value={authType} onChange={(value) => setAuthType(value as ModelProviderAuthType)} options={[{ value: 'api_key', label: t('cluster.models.authApiKey') }, ...(api.scope.kind === 'cluster' ? [{ value: 'service_identity', label: t('cluster.models.authServiceIdentityOption') }] : []), { value: 'none', label: t('cluster.models.authNone') }]} />
              <SelectField label={t('cluster.models.catalogLabel')} value={catalogMode} onChange={(value) => setCatalogMode(value as ModelProviderCatalogMode)} options={[{ value: 'auto', label: t('cluster.models.catalogAuto') }, { value: 'disabled', label: t('cluster.models.catalogDisabled') }]} />
              <span className={styles.headerHint}>{provider?.headers_set ? t('cluster.models.headersConfiguredHint') : t('cluster.models.headersHint')}</span>
              {headerRows.map((row, index) => (
                <div className={styles.headerRow} key={index}>
                  <input className={styles.headerInput} placeholder={t('cluster.models.headerKeyPlaceholder')} value={row.key} autoComplete="off" data-testid={`header-key-${index}`} onChange={(event) => setHeaderRow(index, { key: event.target.value })} />
                  <input className={styles.headerInput} placeholder={t('cluster.models.headerValuePlaceholder')} value={row.value} autoComplete="off" data-testid={`header-value-${index}`} onChange={(event) => setHeaderRow(index, { value: event.target.value })} />
                  <button className={styles.iconButton} type="button" aria-label={t('cluster.models.removeHeader')} onClick={() => setHeaderRows((rows) => rows.filter((_, i) => i !== index))}><Trash size={15} aria-hidden="true" /></button>
                </div>
              ))}
              <Button type="button" variant="ghost" size="sm" data-testid="add-header" onClick={() => { setClearHeaders(false); setHeaderRows((rows) => [...rows, { key: '', value: '' }]); }}><Plus size={13} aria-hidden="true" />{t('cluster.models.addHeader')}</Button>
              {provider?.headers_set && headerRows.length === 0 && (
                <Button type="button" variant={clearHeaders ? 'danger' : 'ghost'} size="sm" data-testid="clear-headers" onClick={() => setClearHeaders((c) => !c)}>{clearHeaders ? t('projectSettings.models.clearHeadersPending') : t('projectSettings.models.clearHeadersAction')}</Button>
              )}
            </div>
          )}
        </div>
        {error && <p className={styles.formError} role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
